import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileDown, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "rosevale_guide_subscriber";

const readSaved = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") || {}; } catch { return {}; }
};

export const GuideDownloadButton = ({
  niche, guide, index = 0, accent = "#0090D0",
  endpoint = "guides/download", eyebrow, title, label = "Download the PDF guide", buttonClassName,
}) => {
  const dialogEyebrow = eyebrow || `${niche?.name} · Free guide`;
  const dialogTitle = title || guide?.title;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => {
    const saved = readSaved();
    return { name: saved.name || "", email: saved.email || "", business_name: saved.business_name || "", consent: false };
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please tick the box so we can send you the guide."); return; }
    setStatus("sending");
    try {
      const payload = { name: form.name, email: form.email, business_name: form.business_name, consent: true };
      if (guide && niche) Object.assign(payload, {
        niche_slug: niche.slug, niche_name: niche.name,
        guide_title: guide.title, guide_summary: guide.summary, guide_points: guide.points,
        guide_checklist: guide.checklist || [], niche_pains: niche.pains || [],
        guide_cta: guide.cta || "",
      });
      const res = await axios.post(`${API}/${endpoint}`, payload, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const filename = (cd.match(/filename="([^"]+)"/) || [])[1] || "rosevale-guide.pdf";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ name: form.name, email: form.email, business_name: form.business_name }));
      setStatus("done");
      toast.success("Your guide is downloading — we've emailed you a copy too.");
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong — please try again.");
    }
  };

  const onOpenChange = (v) => { setOpen(v); if (!v) setTimeout(() => setStatus("idle"), 300); };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`guide-download-btn-${index}`}
        className={buttonClassName || "group mt-7 inline-flex items-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#0090D0] transition-colors duration-300"}
      >
        <FileDown size={16} /> {label}
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md bg-[#FDFBF7] border-[#103080]/10 p-0 overflow-hidden" data-testid="guide-download-dialog">
          <div className="h-1.5 w-full" style={{ backgroundColor: accent }} />
          <div className="p-6 sm:p-8">
            {status === "done" ? (
              <div className="text-center py-4" data-testid="guide-download-success">
                <span className="mx-auto w-14 h-14 rounded-full bg-[#40C0E0]/20 text-[#0090D0] flex items-center justify-center"><CheckCircle2 size={28} /></span>
                <h3 className="font-serif font-semibold text-2xl text-[#103080] mt-5">Your guide is on its way.</h3>
                <p className="text-sm text-[#0F172A]/65 mt-2 leading-relaxed">We've also emailed you a link to the PDF so it's easy to find later. If the download didn't start, use the button below.</p>
                <button type="button" onClick={submit} className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#103080]/15 px-5 py-2.5 text-sm font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300">
                  <Download size={15} /> Download again
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="guide-download-form">
                <DialogHeader className="text-left space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.25em] font-medium" style={{ color: accent }}>{dialogEyebrow}</p>
                  <DialogTitle className="font-serif font-semibold text-2xl text-[#103080] leading-tight">{dialogTitle}</DialogTitle>
                  <DialogDescription className="text-sm text-[#0F172A]/60">Pop your details in — the branded PDF downloads straight away and we'll email you a copy from Luke &amp; Abbie.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={`g-name-${index}`} className="text-xs text-[#103080]">Your name</Label>
                    <Input id={`g-name-${index}`} data-testid="guide-name-input" required value={form.name} onChange={set("name")} placeholder="Jo Bloggs" className="bg-white border-[#103080]/15 focus-visible:ring-[#0090D0]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`g-biz-${index}`} className="text-xs text-[#103080]">Business name <span className="text-[#0F172A]/40">(optional)</span></Label>
                    <Input id={`g-biz-${index}`} data-testid="guide-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Bloggs & Co" className="bg-white border-[#103080]/15 focus-visible:ring-[#0090D0]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`g-email-${index}`} className="text-xs text-[#103080]">Email address</Label>
                  <Input id={`g-email-${index}`} data-testid="guide-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="jo@bloggs.co.uk" className="bg-white border-[#103080]/15 focus-visible:ring-[#0090D0]" />
                </div>
                <label className="flex items-start gap-3 text-xs text-[#0F172A]/65 leading-relaxed cursor-pointer">
                  <Checkbox
                    data-testid="guide-consent-checkbox"
                    checked={form.consent}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v === true }))}
                    className="mt-0.5 border-[#103080]/30 data-[state=checked]:bg-[#103080] data-[state=checked]:border-[#103080]"
                  />
                  <span>I'm happy for Rosevale Accountants to email me this guide plus occasional practical tips and updates. I can unsubscribe at any time.</span>
                </label>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="guide-download-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#103080] text-white text-sm font-medium px-6 py-3.5 hover:bg-[#0090D0] transition-colors duration-300 disabled:opacity-60"
                >
                  {status === "sending" ? <><Loader2 size={16} className="animate-spin" /> Preparing your PDF…</> : <><Download size={16} /> Send me the guide</>}
                </button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
